<template>
  <BaseLocationQuickSearch
    :id="id"
    ref="baseLocationSearchRef"
    v-model="internalValue"
    :locations="locations"
    :errors="errors"
    :append-to="appendTo"
    :disabled="disabled"
    option-label="name"
    complete-on-focus
    @search="onLocationSearch"
    @clear="onClear"
    @blur="onAutoCompleteBlur"
  />
</template>

<script lang="ts" setup>
import { ErrorObject } from '@vuelidate/core';
import { CancelTokenSource, default as axios } from 'axios';
import { PropType, ref, Ref, toRefs, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue, useToast } from '@rygen/rygen-ui/composables';
import { Loadable, Location } from '@rygen/rygen-ui/model';

import api from '@/api';
import { LocationSearchCriteria } from '@/model';

import BaseLocationQuickSearch from '@/components/location/BaseLocationQuickSearch.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Location,
    default: () => new Location(),
  },
  errors: {
    type: Array as PropType<ErrorObject[]>,
    default: () => [],
  },
  appendTo: {
    type: String as PropType<'body' | 'self'>,
    default: () => 'self',
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { id, modelValue, errors, appendTo, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: Location): void;
}>();

const baseLocationSearchRef = useTemplateRef('baseLocationSearchRef');

const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts) => new Location(opts),
});
const toast = useToast();

const locations: Ref<Loadable<Location[]>> = ref({ loading: false });
const cancelRequest: Ref<CancelTokenSource | undefined> = ref(undefined);

const onLocationSearch = async (input: string) => {
  cancelRequest.value?.cancel();
  cancelRequest.value = axios.CancelToken.source();

  try {
    locations.value.loading = true;

    const payload = new LocationSearchCriteria({
      criteria: input,
    });
    const result = await api.shipmentParcel.getPossibleParcelPickupLocations(
      payload,
      cancelRequest.value,
    );
    locations.value.data = result;
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    locations.value.error = e;
    console.error('Error loading locations', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('locations.locations', 2),
      }),
      error: e,
    });
  } finally {
    locations.value.loading = false;
  }
};

const onClear = () => {
  internalValue.value = new Location();
};

const onAutoCompleteBlur = () => {
  // cancel ongoing http request
  cancelRequest?.value?.cancel();
  baseLocationSearchRef.value?.clearSearchTimeout();
};
</script>
